import { Card, Grid } from '@design-systems/canvas-ui'
import { Icon, WarningFillIcon } from '@design-systems/react-icons'
import DOMPurify from 'dompurify'
import { isEmpty } from 'lodash'
import { useEffect, useState } from 'react'
import appConfig from '../config/appConfig'
import { buildBannerText } from '../globalComponents/Builders'

/**
 * HeaderBanner component displays a banner with a caution icon and text.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.bannerText - The text to display inside the banner (used directly if provided).
 * @param {string} props.label_name - The label name for building banner text.
 * @param {Object} props.dataNode - The data node for building banner text.
 * @param {string} props.component_name - The component name for fetching config-based banner text.
 *
 * @returns {JSX.Element} The rendered HeaderBanner component.
 */
const HeaderBanner = ({
  bannerText: directBannerText,
  label_name,
  dataNode,
  component_name,
}) => {
  const [bannerText, setBannerText] = useState(directBannerText || '')

  useEffect(() => {
    // If bannerText is passed directly, use it and skip config fetching
    if (directBannerText) {
      setBannerText(directBannerText)
      return
    }

    async function fetchConfig() {
      if (!component_name) {
        setBannerText(buildBannerText(label_name, dataNode))
        return
      }
      const getConfig = await appConfig()
      const configBannerText =
        getConfig?.mfelcm?.bannerTexts?.[component_name?.toLowerCase()] ?? ''

      if (!configBannerText || isEmpty(configBannerText)) {
        setBannerText(buildBannerText(label_name, dataNode))
      } else {
        setBannerText(configBannerText)
      }
    }
    fetchConfig()
  }, [directBannerText, label_name, dataNode, component_name])

  return (
    <Card
      data-testid="header-banner"
      variant="elevated"
      style={{
        background: 'var(--canvas-background-color--alert)',
      }}
      className="hc8-mv-2x hc8-pv-3x"
    >
      <Grid.Container
        id="gc-header-banner"
        direction="row"
        className="align-centre-left"
      >
        <Grid.Item className="hc8-pr-1x hc8-ml-3x">
          <Icon
            svg={WarningFillIcon}
            aria-hidden
            className="hc8-mr-3x"
            size="lg"
          />
        </Grid.Item>
        <Grid.Item className="hc8-pa-1x">
          <p
            className="hc8-fs-100"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(bannerText),
            }}
          ></p>
        </Grid.Item>
      </Grid.Container>
    </Card>
  )
}

export default HeaderBanner
